import { useEffect, useMemo, useState } from 'react'
import Layout from 'containers/Layout'
import apiGet from 'lib/network/apiGet'
import apiPut from 'lib/network/apiPut'
import apiGetBlob from 'lib/network/apiGetBlob'
import Table from 'components/pageComponents/Table'
import Button from 'components/atoms/Button'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import PageFooter from 'components/paginationComponent'
import Selection from 'components/molecules/Selection'
import { useTranslation } from 'react-i18next'
import useModal from 'hooks/useModal'
import notify from 'components/atoms/Notify'
import { Link } from 'react-router-dom'
import { IoSearch } from 'react-icons/io5'
import _ from 'lodash'
import LeadAssignModal from './components/LeadAssignModal'

const CRM_STAGES = ['new', 'assigned', 'contacted', 'qualified', 'proposal_sent', 'won', 'lost', 'archived']

const Leads = () => {
  const { t } = useTranslation()
  const [list, setList] = useState([])
  const [pagination, setPagination] = useState({})
  const [loading, setLoading] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState(null)
  const [filter, setFilter] = useState({ search: '', stage: '' })
  const [stageDrafts, setStageDrafts] = useState({})

  const fetchLeads = async (page = 1) => {
    const isInitialPage = page === 1
    if (isInitialPage) setLoading(true)

    try {
      const res = await apiGet('crm/leads', { pageNumber: page, pageSize: 20, search: filter.search || undefined, stage: filter.stage || undefined })
      const fetchedList = res?.data?.list || []
      setList(prev => (isInitialPage ? fetchedList : [...prev, ...fetchedList]))
      setPagination(res?.data?.pagination || {})
    } finally {
      if (isInitialPage) setLoading(false)
    }
  }

  useEffect(() => {
    fetchLeads()
  }, [filter.search, filter.stage])

  const [AssignModal, openAssignModal] = useModal({
    content: <LeadAssignModal leadId={selectedLeadId} t={t} close={null} onSuccess={() => fetchLeads(pagination.pageNumber || 1)} />,
    title: t('crm.actions.assign'),
    // height: 200,
    // padding: 'p-4',
  })

  const onAssign = id => {
    setSelectedLeadId(id)
    openAssignModal()
  }

  const onStageUpdate = async (id, stage) => {
    const currentStage = stageDrafts[id] || _.find(list, { _id: id })?.stage
    if (!stage || stage === currentStage) return

    const updateStage = async () => {
      setStageDrafts(prev => ({ ...prev, [id]: stage }))
      const res = await apiPut(`crm/leads/${id}/stage`, { stage })
      if (res?.success) {
        fetchLeads(pagination.pageNumber || 1)
        return
      }

      // Revert optimistic stage when API fails
      setStageDrafts(prev => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    }

    if (_.includes(['lost', 'archived'], stage)) {
      notify.alertConfirm({
        title: t('crm.messages.confirmTitle'),
        message: t('crm.messages.confirmDestructive'),
        onConfirm: updateStage,
      })
      return
    }

    await updateStage()
  }

  const onExport = async () => {
    const blob = await apiGetBlob(`crm/leads/export.csv?search=${encodeURIComponent(filter.search || '')}&stage=${encodeURIComponent(filter.stage || '')}`)
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'crm-leads.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const stageOptions = useMemo(
    () =>
      CRM_STAGES.map(stage => ({
        id: stage,
        name: t(`crm.stages.${stage}`),
      })),
    [t]
  )

  const columns = useMemo(
    () => [
      { name: t('crm.columns.officeName'), key: 'officeName', rendered: item => item.officeName },
      { name: t('crm.columns.commercialRegisterName'), key: 'commercialRegisterName', rendered: item => item.commercialRegisterName },
      { name: t('crm.columns.commercialRegisterNumber'), key: 'commercialRegisterNumber', rendered: item => item.commercialRegisterNumber },
      { name: t('crm.columns.ownerName'), key: 'ownerName', rendered: item => item.ownerName },
      { name: t('crm.columns.email'), key: 'email', rendered: item => item.email },
      { name: t('crm.columns.phoneNumber'), key: 'phoneNumber', rendered: item => item.phoneNumber },
      {
        name: t('crm.columns.minimumPriceAllowedForPromotion'),
        key: 'minimumPriceAllowedForPromotion',
        rendered: item => item.minimumPriceAllowedForPromotion ?? '-',
      },
      { name: t('crm.columns.tenantLowestAge'), key: 'tenantLowestAge', rendered: item => item.tenantLowestAge ?? '-' },
      { name: t('crm.columns.tenantHighestAge'), key: 'tenantHighestAge', rendered: item => item.tenantHighestAge ?? '-' },
      { name: t('crm.columns.deliveryPrice'), key: 'deliveryPrice', rendered: item => item.deliveryPrice ?? '-' },
      {
        name: t('crm.columns.subscriptionType'),
        key: 'subscriptionType',
        rendered: item => t(`crm.packages.${item.subscriptionType}`) || item.subscriptionType,
      },
      { name: t('crm.columns.registeredForVat'), key: 'registeredForVat', rendered: item => (item.registeredForVat ? t('common.yes') : t('common.no')) },
      {
        name: t('crm.columns.stage'),
        key: 'stage',
        rendered: item => (
          <Selection
            className="w-[160px]"
            value={stageDrafts[item._id] || item.stage}
            onChange={option => onStageUpdate(item._id, option?.id || item.stage)}
            list={stageOptions}
            title={t('crm.filters.stage')}
            dropdownPlacement="bottom"
            usePortalOptions
          />
        ),
      },
      { name: t('crm.columns.assignedTo'), key: 'assignedUser', rendered: item => item.assignedUser?.fullName || '-' },
      { name: t('crm.columns.createdAt'), key: 'createdAt', rendered: item => new Date(item.createdAt).toLocaleString() },
      {
        name: t('crm.columns.actions'),
        key: 'actions',
        rendered: item => (
          <div className="flex items-center gap-2">
            <Link to={`/crm/leads/${item._id}`}>
              <Button size="sm" variant="outline" className="h-8 px-3">
                {t('crm.actions.view')}
              </Button>
            </Link>
            <Button size="sm" variant="outline" className="h-8 px-3" type="button" onClick={() => onAssign(item._id)}>
              {t('crm.actions.assign')}
            </Button>
          </div>
        ),
      },
    ],
    [t, stageOptions, stageDrafts]
  )

  return (
    <Layout
      title={t('crm.title')}
      headerRight={
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full justify-end sm:mt-0 sm:ml-4 gap-2 sm:gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <PageFooter currentPage={pagination.pageNumber} totalPages={pagination.totalPages} totalRecords={pagination.totalItems} />
            <div className="mt-1 relative rounded-md shadow-sm flex-1 sm:flex-none">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                <IoSearch className="mr-3 h-4 w-4 text-gray-400" aria-hidden="true" />
              </div>
              <input
                className="block w-full pl-9 shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm"
                placeholder={t('crm.filters.search')}
                value={filter.search}
                onChange={e => setFilter(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>
          </div>
          <Selection
            className="w-[140px]"
            value={filter.stage}
            onChange={option =>
              setFilter(prev => ({
                ...prev,
                stage: prev.stage === option?.id ? '' : option?.id || '',
              }))
            }
            list={stageOptions}
            title={t('crm.filters.stage')}
          />
          <Button onClick={onExport}>{t('crm.actions.export')}</Button>
          <Link to="/crm/leads/add">
            <Button>{t('crm.actions.add')}</Button>
          </Link>
        </div>
      }
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {loading ? (
                  <div className="h-[220px] flex items-center justify-center">
                    <LoadingIcon />
                  </div>
                ) : list?.length === 0 ? (
                  <div className="py-10">
                    <EmptyState v2 description={t('common.noData')} />
                  </div>
                ) : (
                  <Table
                    columns={columns}
                    data={list}
                    fetchData={() => fetchLeads((pagination?.pageNumber || 1) + 1)}
                    hasMore={pagination?.pageNumber !== pagination?.totalPages}
                  />
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <AssignModal />
    </Layout>
  )
}

export default Leads
